/**
 * StatusDot / StatusPill — bioluminescent status primitives (Living Interface).
 * One source for status color + glow; replaces scattered local color maps.
 */
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { TYPE } from "@/lib/typography";
import { STATUS_TONES, toneForStatus, type StatusTone } from "@/lib/statusColors";

interface DotProps {
  tone?: StatusTone;
  status?: string;
  /** Gentle pulse for "live" states. */
  pulse?: boolean;
  size?: number;
  className?: string;
}

export function StatusDot({ tone, status, pulse = false, size = 8, className }: DotProps) {
  const t = STATUS_TONES[tone ?? toneForStatus(status)];
  return (
    <motion.span
      aria-hidden
      className={cn("inline-block rounded-full", className)}
      style={{
        width: size,
        height: size,
        background: t.color,
        boxShadow: `0 0 0 1px ${t.color}, 0 0 8px 1px ${t.glow}`,
      }}
      animate={pulse ? { opacity: [1, 0.55, 1], scale: [1, 0.92, 1] } : undefined}
      transition={pulse ? { duration: 2.2, repeat: Infinity, ease: "easeInOut" } : undefined}
    />
  );
}

interface PillProps {
  tone?: StatusTone;
  status?: string;
  label?: string;
  pulse?: boolean;
  className?: string;
}

export function StatusPill({ tone, status, label, pulse, className }: PillProps) {
  const resolved = tone ?? toneForStatus(status);
  const t = STATUS_TONES[resolved];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5",
        TYPE.badge,
        t.bgClass,
        t.textClass,
        className,
      )}
    >
      <StatusDot tone={resolved} pulse={pulse} size={6} />
      <span className="uppercase tracking-wide">{label ?? status ?? resolved}</span>
    </span>
  );
}
